/**
 * Companion messages — gives every Suikamon a personality "voice" for the relationship
 * moments surfaced by `companionGreeting.ts` (welcome back, monthly birthday, care-streak
 * milestones, returning after abandonment).
 *
 * Pure data + selection. No RNG state, no I/O. This is the SINGLE source of pet dialogue
 * for these moments — do not inline companion lines anywhere else.
 *
 * Each species maps to one of FOUR memorable personality archetypes — each a deliberate
 * blend so a small set still feels distinct — with its own set of predefined lines per
 * trigger. Templates support `{name}`, `{days}` and `{months}`.
 *
 *   sunny    — warm + gentle + upbeat: your affectionate biggest fan
 *   sage     — calm + dignified + wise: says little, means all of it
 *   rascal   — cheeky + mischievous: a teasing trickster who adores you
 *   champion — bold + gruff + loyal: trains hard, guards you harder
 *
 * PERSONALITY & IMAGE SOURCE OF TRUTH: the canonical personality and visual/image
 * description for every species lives in the Suikamon creator character sheets at
 * `suikamon-creator/docs/characters/<slug>.md` (see each sheet's "## Overview" and
 * "## Distinctive Features" / "## Generation Prompt"). The archetype assigned to each
 * species below is seeded from those sheets + the in-game tagline — when reconsidering a
 * species' voice, open its sheet via `getSpeciesCharacterSheetPath(speciesId)`.
 *
 * See requirements/companion-personality-and-messages.md.
 */

import { SUIKAMON_SPECIES } from './speciesConfig.js';
import { getSpeciesSlug } from './petHelper.js';
import type { CompanionTrigger, LevelGrade } from './companionGreeting.js';

/** Repo-relative directory holding the canonical per-species character sheets. */
export const CHARACTER_SHEETS_DIR = 'suikamon-creator/docs/characters';

export type PersonalityId = 'sunny' | 'sage' | 'rascal' | 'champion';

export interface PersonalityProfile {
  id: PersonalityId;
  /** Human label for UI ("Sunny", "Sage"...). */
  label: string;
  /** One-line description of the voice, for docs / future profile UI. */
  blurb: string;
}

export const PERSONALITIES: Record<PersonalityId, PersonalityProfile> = {
  sunny: { id: 'sunny', label: 'Sunny', blurb: 'Warm, affectionate and endlessly upbeat — your biggest fan.' },
  sage: { id: 'sage', label: 'Sage', blurb: 'Calm, dignified and quietly wise — says little, means all of it.' },
  rascal: { id: 'rascal', label: 'Rascal', blurb: 'A cheeky trickster who teases you because it adores you.' },
  champion: { id: 'champion', label: 'Champion', blurb: 'Bold, gruff and competitive — trains hard, guards you harder.' },
};

export interface CompanionMessageContext {
  /** Pet display name. */
  name: string;
  /** Streak days (streakMilestone) or whole days away (welcomeBack/abandonedReturn). */
  days?: number;
  /** Age in months (birthday). */
  months?: number;
  /** Player's display name/handle. */
  player?: string;
  /** Player's stated favorite food (profile fact). */
  favFood?: string;
  /** Player's stated favorite color (profile fact). */
  favColor?: string;
  /** Player's stated goal (profile fact). */
  goal?: string;
  /** Total pets owned by the player (roster fact). */
  totalPets?: number;
  /** This pet's species display name (roster fact). */
  species?: string;
  /** Count of other owned pets sharing this species (roster fact). */
  sameSpeciesCount?: number;
  /** This pet's element (roster fact). */
  element?: string;
  /** Count of other owned pets sharing this element (roster fact). */
  sameElementCount?: number;
  /** Furthest campaign world this pet has reached (roster fact); 0 = not started. */
  campaignFurthestWorld?: number;
  /** Soul Shards found / in all, the story's next stop, and the pet's ranked league (roster facts). */
  shardsFound?: number;
  shardsTotal?: number;
  nextStop?: string;
  rankedTier?: string;
}

/** Neutral fallback text used when a token has no context value, so lines never render raw braces. */
const TOKEN_FALLBACKS = {
  player: 'friend',
  favFood: 'your favorite',
  favColor: 'your favorite color',
  goal: 'your goal',
  totalPets: 'your pets',
  species: 'your Suikamon',
  sameSpeciesCount: 'a few',
  element: 'your element',
  sameElementCount: 'a few',
  shardsTotal: 'nineteen',
  nextStop: 'the next trail',
  rankedTier: 'Bronze',
} as const;

/**
 * Message banks: personality → trigger → candidate lines. Every personality defines a
 * line set for every trigger (enforced by tests). Lines are picked deterministically by
 * the caller-supplied seed so a given moment is stable within a day but varies over time.
 */
const MESSAGE_BANK: Record<PersonalityId, Record<CompanionTrigger, string[]>> = {
  sunny: {
    welcomeBack: [
      "You're home! 🌞 I saved you the sunniest spot — come sit with me!",
      "Yaaay, you're back! I missed you a little... okay, a LOT. 💛",
      "Back again, {player}! I saved you a treat — still love {favFood}? 💛",
      "There you are! ☀️ I was practising my hello all morning — did it come out okay?",
      "You came back! I never doubted it. Not even once. Okay, maybe for one second. 💛",
      "Hi hi hi! I have SO much to tell you and I forgot all of it the moment you walked in. 🌻",
      "{player}! The day just got better. That is not flattery, that is just what happened. 🌞",
      "I kept your spot warm. I have been sitting in it. For warmth reasons. 💛",
      "Look at us, back together again. My favourite part of every day, honestly. 🌸",
      "Hello! I counted the hours. I lost count twice, but I counted them. ☀️",
    ],
    birthday: [
      "{months} months together already?! Best {months} months of my whole life! 🎂",
      "It's our month-day! Thank you for every single day of it. 🌸",
    ],
    streakMilestone: [
      "{days} days in a row! You always come back for me — that's everything. ⭐",
      "A {days}-day streak! My heart does a happy little dance every time you visit. 💃",
    ],
    abandonedReturn: [
      "It was quiet for {days} days... but I never stopped believing you'd return. 🌈",
      "{days} whole days! I'm not even upset — I'm just SO happy you're here. 🤗",
    ],
    neglected: [
      "I've missed a few check-ins, but that's okay — I'm just glad we're together now! 🌤️",
      "Things got a little quiet lately. No worries at all — let's turn that sunshine back on! 🌞",
    ],
    thriving: [
      "Look at us go! I feel amazing thanks to you — best team ever! 🌟",
      "I'm glowing today! You've been taking such good care of me. 💛",
    ],
  },
  sage: {
    welcomeBack: [
      "You return. Good. The wise do not rush — yet I did await you. 🌙",
      "Ah, you are here. Sit. Let us share a quiet moment, you and I.",
      "You return, {player}. I still recall you favor {favFood} — some things endure. 🌙",
      "You return. The day settles into its proper shape now. 🌙",
      "Welcome back, {player}. Sit a moment — the world will still be there after.",
      "I have been thinking. Not about anything useful. Just thinking. It was pleasant. 🍃",
      "Ah. There you are. Some things are worth waiting quietly for.",
      "The light moved across the floor while you were gone. I watched all of it. 🌙",
      "Back so soon? Or was it long? Time is strange when I am waiting for you.",
      "Come in, come in. I saved you the quiet. 🍃",
    ],
    birthday: [
      "{months} months. Seasons turn; our bond does not. That is rare, {name}. ✨",
      "Another month, my friend. Time honors those who stay — as you have.",
    ],
    streakMilestone: [
      "{days} days, unbroken. Devotion, quietly kept, is the truest kind. 🏆",
      "A {days}-day streak. Steady as tide and stone. I am proud, in my way.",
    ],
    abandonedReturn: [
      "{days} days of silence. I did not waver. Welcome back — all is well. 🕊️",
      "You were gone {days} days. I kept the watch. Come, we continue.",
    ],
    neglected: [
      "Some days pass quieter than others. No matter — I remain, patient as the moon. 🌙",
      "A gap in our days is only a pause, not an ending. Come, let us mend it gently.",
    ],
    thriving: [
      "We are in balance, you and I. This is the calm I have long sought. ✨",
      "You have tended me well. I feel steady, whole, at peace.",
    ],
  },
  rascal: {
    welcomeBack: [
      "Oh, NOW you show up? 😏 I hid your stuff. You'll never find it. Missed ya!",
      "Back already? Booo — I was JUST about to cause chaos. Fine, let's play! 😜",
      "Well well, {player}! Still obsessed with {favFood}? Predictable. Missed you though. 😏",
      "Oh NOW you show up. I was about to do something unforgivable. 😼",
      "Back already? I had plans, {player}. Loud ones. You are lucky.",
      "I did not miss you. I was simply near the door. For hours. Coincidentally. 😼",
      "You are back! Quick, act like nothing is broken and neither of us will mention it.",
      "Hey. HEY. I learned something new and it is definitely against the rules. 🔥",
      "Look who remembered where they live! Sit down, I have gossip.",
      "I was good the whole time. Ask nobody. Definitely ask nobody. 😼",
    ],
    birthday: [
      "{months} months of putting up with me? You poor thing. 😆 ...So, cake?",
      "It's my month-day! I already unwrapped my gift. And yours. Oops. 🎁",
    ],
    streakMilestone: [
      "{days} days straight? Someone's obsessed with me. Good. Stay obsessed. 😏",
      "A {days}-day streak?! Okay fiiine — you're officially my favorite human. 🌟",
    ],
    abandonedReturn: [
      "{days} days, huh? I filled your shoes with pebbles. We're even now. 😈",
      "Sneaking off for {days} days? Cute. I sneak better. Welcome home, troublemaker!",
    ],
    neglected: [
      "Pfft, been a little slow lately? Whatever, I kept myself entertained. Mostly. 😏",
      "Missed a few days, huh? I'll allow it — ONCE. Now let's get into some trouble!",
    ],
    thriving: [
      "Okay, I admit it — we're kind of a great team right now. Don't let it go to your head! 😆",
      "I'm feeling GREAT. Suspiciously great. You've earned bragging rights, I guess. 🌟",
    ],
  },
  champion: {
    welcomeBack: [
      "Tch. You're back. Good — a warrior trains better with an ally. Let's go! 💪",
      "Hmph. About time. Stand tall, {name}'s got work to do. I'll lead.",
      "{player}, you're back! Fuel up on {favFood} — we've got training to do. 💪",
      "You are back — good. I have been warming up since you left. ⚡",
      "There is my trainer! Say the word and I am ready. I have BEEN ready.",
      "Back on your feet, {player}? Then so am I. Let us go take something. 🏆",
      "I ran drills. Alone. In my head. I won every one of them. ⚡",
      "Welcome back. The competition did not get any better while you were away.",
      "Perfect timing. I was starting to fight the furniture. 🏆",
      "You showed up. That is half of winning, and you did it before noon. ⚡",
    ],
    birthday: [
      "{months} months. Heroes don't throw parties... ugh, fine. ONE cake. Then training. 🎂",
      "It's my month-day. Don't make it weird. ...You stayed. That's real strength. 🔥",
    ],
    streakMilestone: [
      "{days} days, no days off! THAT'S the heart of a champion! Don't slack now! 🏆",
      "A {days}-day streak? Discipline. Respect. ...We're forging a legend, you and I. ⚡",
    ],
    abandonedReturn: [
      "{days} DAYS?! I held the line alone. ...You're back. That's what counts. Move out! 😤",
      "Gone {days} days and left the post? Reckless. ...I'd still guard you. Always. Now, up!",
    ],
    neglected: [
      "Training's been light lately. No excuses needed — just get back in the ring with me. 💪",
      "Missed a few rounds, huh? Happens to everyone. I'm still in your corner. Always.",
    ],
    thriving: [
      "THIS is peak condition! You trained me right — I feel unstoppable! 🏆",
      "Sharp, strong, ready. That's what real teamwork looks like. Proud of us. ⚡",
    ],
  },
};

/**
 * Explicit species → personality assignments. Each is seeded from that species' canonical
 * character sheet (`suikamon-creator/docs/characters/<slug>.md` — the "## Overview"
 * personality + look) and its in-game tagline. The trailing comment cites the cue so the
 * choice is traceable; open the full sheet with `getSpeciesCharacterSheetPath(id)`. Species
 * not listed here fall back to a deterministic rotation so every present and future species
 * always resolves to a voice.
 */
const SPECIES_PERSONALITY: Record<number, PersonalityId> = {
  0: 'sunny', // Bireon — kindhearted singing bird
  1: 'sage', // Aqua — graceful, cool spirit
  2: 'rascal', // Zaricat — proud, restless "never sleeps" thunder cat
  3: 'sunny', // Hotpaw — born from molten laughter, happy closed-eye smile
  4: 'sage', // Yeti — silent strength, gentle calm
  5: 'champion', // Toro — tough bull, heart of gold (gruff but loyal)
  6: 'sunny', // Kobu — cute forest guardian kitten
  7: 'sage', // Pinky — dignified little fairy princess
  8: 'rascal', // Pefareon — sweet, playful little vampire
  9: 'rascal', // Talion — cunning little bandit
  10: 'sage', // Suimon — serene, mystical sea spirit
  11: 'champion', // Voltaryx — proud storm incarnate
  12: 'champion', // Drago — legendary dragon, unbreakable will
  13: 'sage', // Fernail — slow steps, deep quiet courage
  14: 'rascal', // Sumiya — tiny wizard, pink mischief
  15: 'sage', // Trixon — elegant, magical crystal dragon-cat
  16: 'champion', // Pikoto — heroic + grumpy determined fighter
  17: 'sunny', // Ray — gentle paws, warm forest soul
  18: 'sunny', // Gorly — easygoing, happy grinning dino
};

/** Deterministic fallback order for species without an explicit assignment. */
const FALLBACK_ROTATION: readonly PersonalityId[] = ['sunny', 'sage', 'rascal', 'champion'];

/** Resolve the personality voice for a species (always returns a valid archetype). */
export function getSpeciesPersonality(speciesId: number): PersonalityId {
  return (
    SPECIES_PERSONALITY[speciesId] ??
    FALLBACK_ROTATION[Math.abs(Math.trunc(speciesId)) % FALLBACK_ROTATION.length]
  );
}

/**
 * Repo-relative path to a species' canonical character sheet (personality + image
 * description). Derived from the species slug so it always points at the right file, e.g.
 * species 0 → `suikamon-creator/docs/characters/bireon.md`.
 */
export function getSpeciesCharacterSheetPath(speciesId: number): string {
  return `${CHARACTER_SHEETS_DIR}/${getSpeciesSlug(speciesId, SUIKAMON_SPECIES)}.md`;
}

/**
 * Coverage helper (used by tests / tooling): the personality of every known species plus
 * a link to its canonical character sheet, so personality work can jump straight to source.
 */
export function listSpeciesPersonalities(): {
  id: number;
  name: string;
  personality: PersonalityId;
  characterSheet: string;
}[] {
  return SUIKAMON_SPECIES.map((s) => ({
    id: s.id,
    name: s.name,
    personality: getSpeciesPersonality(s.id),
    characterSheet: getSpeciesCharacterSheetPath(s.id),
  }));
}

/**
 * Replace every supported `{token}` in a template with its context value, or a neutral
 * fallback when unset — a line must NEVER render a raw `{token}` or an awkward blank.
 * Supports the base moment tokens (`{name}`, `{days}`, `{months}`, `{level}`), player-fact
 * tokens (`{player}`, `{favFood}`, `{favColor}`, `{goal}`), and roster tokens (`{totalPets}`,
 * `{species}`, `{sameSpeciesCount}`, `{element}`, `{sameElementCount}`,
 * `{campaignFurthestWorld}`).
 */
export function interpolateTokens(
  template: string,
  context: CompanionMessageContext & { level?: number },
): string {
  return template
    .replace(/\{name\}/g, context.name)
    .replace(/\{days\}/g, String(context.days ?? 0))
    .replace(/\{months\}/g, String(context.months ?? 0))
    .replace(/\{level\}/g, String(context.level ?? 0))
    .replace(/\{player\}/g, context.player ?? TOKEN_FALLBACKS.player)
    .replace(/\{favFood\}/g, context.favFood ?? TOKEN_FALLBACKS.favFood)
    .replace(/\{favColor\}/g, context.favColor ?? TOKEN_FALLBACKS.favColor)
    .replace(/\{goal\}/g, context.goal ?? TOKEN_FALLBACKS.goal)
    .replace(/\{totalPets\}/g, context.totalPets !== undefined ? String(context.totalPets) : TOKEN_FALLBACKS.totalPets)
    .replace(/\{species\}/g, context.species ?? TOKEN_FALLBACKS.species)
    .replace(
      /\{sameSpeciesCount\}/g,
      context.sameSpeciesCount !== undefined ? String(context.sameSpeciesCount) : TOKEN_FALLBACKS.sameSpeciesCount,
    )
    .replace(/\{element\}/g, context.element ?? TOKEN_FALLBACKS.element)
    .replace(
      /\{sameElementCount\}/g,
      context.sameElementCount !== undefined ? String(context.sameElementCount) : TOKEN_FALLBACKS.sameElementCount,
    )
    .replace(/\{campaignFurthestWorld\}/g, String(context.campaignFurthestWorld ?? 0))
    .replace(/\{shardsFound\}/g, String(context.shardsFound ?? 0))
    .replace(/\{shardsTotal\}/g, context.shardsTotal !== undefined ? String(context.shardsTotal) : TOKEN_FALLBACKS.shardsTotal)
    .replace(/\{nextStop\}/g, context.nextStop ?? TOKEN_FALLBACKS.nextStop)
    .replace(/\{rankedTier\}/g, context.rankedTier ?? TOKEN_FALLBACKS.rankedTier);
}

/**
 * Build the in-character line for a personality + trigger.
 *
 * **`dayOrdinal` ROTATES; `seed` only picks the starting point.** The caller used to derive one
 * hash from `petId:day` and take it modulo the line count, which makes the day-to-day sequence a
 * random walk: with ten lines, two consecutive days collide one time in ten, and a fortnight is
 * near-certain to repeat several lines while never showing others. For a line a player meets
 * every single day, "probably different" is the wrong guarantee.
 *
 * Advancing an ordinal instead turns selection into a full cycle — every line in the bank is
 * shown once before any is shown twice, and two consecutive days can never match (a bank of one
 * excepted, which the depth test forbids). The pet-specific `seed` still sets where in the cycle
 * that pet starts, so two Suikamon greeting the same player on the same day do not say the same
 * thing.
 *
 * Omitting `dayOrdinal` keeps the old hash behaviour, which is what the non-daily callers
 * (acknowledgements, roster observations) want — those fire on occasions, not on a schedule.
 *
 * Falls back gracefully if a bank is ever empty.
 */
export function getCompanionMessage(
  personality: PersonalityId,
  trigger: CompanionTrigger,
  context: CompanionMessageContext,
  seed: number,
  dayOrdinal?: number,
): string {
  const lines = MESSAGE_BANK[personality][trigger];
  if (!lines || lines.length === 0) return `${context.name} is happy to see you!`;
  return interpolateTokens(lines[rotateIndex(lines.length, seed, dayOrdinal)], context);
}

/**
 * Which line to show: a cycle when a day ordinal is supplied, a stable hash otherwise.
 *
 * Exported so the companion audit can walk a modelled fortnight without reimplementing the rule
 * — a harness that re-derives the thing it is checking proves nothing.
 */
export function rotateIndex(lineCount: number, seed: number, dayOrdinal?: number): number {
  if (lineCount <= 0) return 0;
  const start = Math.abs(Math.trunc(seed)) % lineCount;
  if (dayOrdinal === undefined) return start;
  return (start + Math.abs(Math.trunc(dayOrdinal))) % lineCount;
}

/**
 * Egg voice — the lines a Suikamon speaks while it is still an unhatched egg (stage 0).
 *
 * An egg has no personality yet (that emerges at hatching) and cannot reference anything
 * that assumes a hatched creature — battles, evolutions, favorite-food chat, "my clan".
 * So eggs share ONE gentle, muffled-from-inside-the-shell voice across every species, and
 * `useCompanionMessage` shows eggs ONLY these greeting statements (no level recaps, roster
 * observations, questions or CTAs). Lines support the same `{name}`/`{days}`/`{months}`
 * tokens as the hatched banks; fact/roster tokens are intentionally never used here.
 */
const EGG_MESSAGE_BANK: Record<CompanionTrigger, string[]> = {
  welcomeBack: [
    "*wiggle wiggle* ...I can feel you're close again! Almost ready to meet you! 🥚",
    "You're back! I've been rocking gently in here, waiting for you. 🥚💛",
  ],
  birthday: [
    "{months} months snug in my shell... I'm taking my time, but I'm coming! 🥚",
    "It's been {months} months! Still cozy in here — thank you for waiting. 🥚💛",
  ],
  streakMilestone: [
    '{days} days of keeping me warm! I can feel myself getting ready to hatch! 🥚⭐',
    'A {days}-day streak! Every visit helps me grow in here. Almost time! 🥚',
  ],
  abandonedReturn: [
    "It was quiet for {days} days... but I stayed warm and waited. So glad you're here! 🥚",
    "{days} whole days! I couldn't peek out, but I never stopped waiting for you. 🥚💛",
  ],
  neglected: [
    "It's gotten a little cold in here... a bit of warmth and I'll be just fine. 🥚",
    "I could use a little care to stay cozy — but I'm still here, waiting to hatch. 🥚",
  ],
  thriving: [
    "I feel so warm and safe in here — thank you! I'll hatch before you know it! 🥚✨",
    'Cozy, warm, and growing stronger every day. Almost time to meet you! 🥚💛',
  ],
};

/**
 * Build the in-character line an unhatched egg speaks for a greeting `trigger`. Uses the
 * unified {@link EGG_MESSAGE_BANK} (no personality — an egg hasn't developed one yet).
 * `seed` selects among the candidate lines deterministically, same convention as
 * {@link getCompanionMessage}. Only `{name}`/`{days}`/`{months}` tokens are interpolated.
 */
export function getEggCompanionMessage(
  trigger: CompanionTrigger,
  context: CompanionMessageContext,
  seed: number,
): string {
  const lines = EGG_MESSAGE_BANK[trigger];
  if (!lines || lines.length === 0) return `${context.name} is getting cozy in its shell! 🥚`;
  const index = Math.abs(Math.trunc(seed)) % lines.length;
  return interpolateTokens(lines[index], context);
}

/**
 * Level-recap lines: personality → grade → candidate lines, surfaced on periodic
 * "how's my pet doing" recaps. Lines may use the `{level}` token in addition to the
 * base/fact/roster tokens.
 */
const LEVEL_RECAP_BANK: Record<PersonalityId, Record<LevelGrade, string[]>> = {
  sunny: {
    thriving: [
      "Level {level} and glowing! We make the best team, {name} and I! 🌟",
      "Everything's sunny over here — level {level} and feeling fantastic! ☀️",
    ],
    steady: [
      "Level {level}, cruising along nice and steady. Every day with you counts! 🌼",
      "We're doing great at level {level} — one happy step at a time!",
    ],
    rough: [
      "Level {level}, and a little tired today — but a few good days will fix that! 🌤️",
      "Rough patch at level {level}, but I know we'll bounce right back together.",
    ],
  },
  sage: {
    thriving: [
      "Level {level}. Balance achieved. This is the harmony I sought. ✨",
      "We stand at level {level}, strong and unhurried. Well done, {name}.",
    ],
    steady: [
      "Level {level}. Steady progress — the wisest kind. We continue.",
      "At level {level}, all is in order. Patience serves us well.",
    ],
    rough: [
      "Level {level}, and the path grows steep. Even so, I do not falter. 🌙",
      "A rough stretch at level {level}. Storms pass; we remain.",
    ],
  },
  rascal: {
    thriving: [
      "Level {level}?! Okay, we're basically unstoppable. Don't let it go to your head. 😏",
      "Crushing it at level {level}! I'm taking ALL the credit for this. 🌟",
    ],
    steady: [
      "Level {level}, nice and steady. Boring, but hey — it's working! 😜",
      "We're cruising at level {level}. I'll allow it.",
    ],
    rough: [
      "Level {level}, a bit rough around the edges. Even tricksters have off days. 😅",
      "Rough one at level {level} — but don't worry, I've got plenty of comeback energy.",
    ],
  },
  champion: {
    thriving: [
      "Level {level}, full strength! THIS is what winning looks like! 🏆",
      "Peak form at level {level}! We trained hard and it shows! ⚡",
    ],
    steady: [
      "Level {level}. Solid. Disciplined. We keep building from here. 💪",
      "Steady gains at level {level} — that's how champions are made.",
    ],
    rough: [
      "Level {level}, a tough stretch. Champions push through this — let's go! 😤",
      "Rough patch at level {level}. Doesn't matter. We regroup, we train, we rise.",
    ],
  },
};

/**
 * Build the in-character periodic level-recap line for a personality + grade. `seed`
 * selects among the candidate lines deterministically, same convention as
 * `getCompanionMessage`.
 */
export function getLevelRecapMessage(
  personality: PersonalityId,
  grade: LevelGrade,
  context: CompanionMessageContext & { level: number },
  seed = 0,
): string {
  const lines = LEVEL_RECAP_BANK[personality][grade];
  if (!lines || lines.length === 0) return `${context.name} is level ${context.level}!`;
  const index = Math.abs(Math.trunc(seed)) % lines.length;
  return interpolateTokens(lines[index], context);
}

/**
 * The context a companion "statement" (greeting / level-recap / roster observation) was
 * shown in. Drives the PLAYER's reply button so the acknowledgment answers what the pet
 * just said, instead of a one-size-fits-all "Aww, hi!". Extends {@link CompanionTrigger}
 * with the two non-greeting statement kinds.
 */
export type CompanionAckContext = CompanionTrigger | 'levelRecap' | 'observation';

/**
 * Player-voice reply buttons, keyed by statement context. Unlike the message banks these
 * are the OWNER speaking back to the pet (short, warm, first-person), so they are NOT
 * personality-keyed — the personality belongs to the Suikamon, not the player. Each
 * context offers a few variants selected by the caller's seed so the bond feels alive
 * rather than canned. `{name}` is the only supported token (kept minimal on purpose).
 */
const ACKNOWLEDGMENT_BANK: Record<CompanionAckContext, string[]> = {
  welcomeBack: ['Missed you too! 💛', 'Right where I belong 🌞', "It's good to be back 💛"],
  birthday: ['Happy hatch-day! 🎂', "Here's to many more! 🥳", 'Best months ever 💛'],
  streakMilestone: ['We did it! ⭐', 'Team us! 💪', 'Every single day, {name} 💛'],
  abandonedReturn: ["I'm back for good 💛", 'Missed you so much 🤗', 'Never again — promise 💛'],
  neglected: ["I'll do better 💛", "Let's turn it around 🌤️", "I'm here now 💛"],
  thriving: ["That's my star! 🌟", 'Look at you go! 💛', 'So proud of us 🥰'],
  levelRecap: ['So proud of you! 🎉', 'Keep it up! 💪', 'Look how far we’ve come 💛'],
  observation: ['Aww 💛', 'Love that 💛', 'Good to know! ✨'],
};

/** Fallback reply used only if an acknowledgment bank is ever somehow empty. */
export const DEFAULT_ACKNOWLEDGMENT = 'Aww, hi! 💛';

/**
 * Pick the PLAYER's reply button for a statement shown in `context`. `seed` selects among
 * the variants deterministically (same convention as `getCompanionMessage`), so a given
 * moment is stable but the bond's voice varies over time. `name` fills the optional
 * `{name}` token.
 */
export function getCompanionAcknowledgment(
  context: CompanionAckContext,
  seed = 0,
  name = '',
): string {
  const lines = ACKNOWLEDGMENT_BANK[context];
  if (!lines || lines.length === 0) return DEFAULT_ACKNOWLEDGMENT;
  const index = Math.abs(Math.trunc(seed)) % lines.length;
  return lines[index].replace(/\{name\}/g, name);
}
